"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import styled from "styled-components";
import { Button } from "@lmn/design-system";

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  text-align: center;
  margin-top: 100px;
`;

const ContentWrapper = styled.div`
  max-width: 500px;
  margin: 0 auto;
`;

const ErrorTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) =>
    theme.kallisto.color.text.static.textSecondary || "#666"};
  margin-bottom: 2rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const DebugContainer = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background-color: ${({ theme }) =>
    theme.kallisto.color.background.static.backgroundLayer01 || "#f0f0f0"};
  border-radius: 4px;
  text-align: left;
`;

const DebugTitle = styled.p`
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const ErrorDetail = styled.p`
  color: ${({ theme }) =>
    theme.kallisto.color.text.static.textWarning || "#f0f0f0"};
  font-size: 0.875rem;
  font-family: monospace;
  overflow-x: auto;
`;

export default function ErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isProduction = process.env.NEXT_PUBLIC_APP_ENV === "production";

  useEffect(() => {
    // Log the error to Sentry in production
    if (isProduction) {
      Sentry.captureException(error);
    } else {
      console.error("Error:", error);
    }
  }, [error, isProduction]);

  return (
    <ErrorContainer>
      <ContentWrapper>
        <ErrorTitle>Something went wrong</ErrorTitle>
        <ErrorMessage>
          We apologize for the inconvenience. Our team has been notified about
          this issue.
        </ErrorMessage>
        <ButtonsContainer>
          <Button variant="primary" onClick={() => reset()}>
            Try again
          </Button>
          <Button
            variant="secondary"
            appearance="outlined"
            onClick={() => {
              window.location.href = "/";
            }}
          >
            Go to homepage
          </Button>
        </ButtonsContainer>

        {!isProduction && (
          <DebugContainer>
            <DebugTitle>
              Error details (visible in development only):
            </DebugTitle>
            <ErrorDetail>{error.message}</ErrorDetail>
          </DebugContainer>
        )}
      </ContentWrapper>
    </ErrorContainer>
  );
}
